import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PaymentIcon from "@material-ui/icons/Payment";

import { i18n } from "../../translate/i18n";
import { CLOUD_PAYMENT_REQUIRED_EVENT } from "../../utils/openCloudPaymentRequiredDialog";

const META_BILLING_URL = "https://business.facebook.com/billing_hub/";

const useStyles = makeStyles((theme) => ({
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(5),
    position: "relative",
  },
  titleIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(244, 67, 54, 0.12)",
    color: theme.palette.error.main,
    flexShrink: 0,
  },
  intro: {
    marginTop: theme.spacing(1),
    lineHeight: 1.55,
    color: theme.palette.text.primary,
  },
  stepsTitle: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  stepsList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    "& li + li": {
      marginTop: theme.spacing(0.75),
    },
  },
  footer: {
    marginTop: theme.spacing(2.5),
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
  },
  billingLink: {
    display: "inline-block",
    marginTop: theme.spacing(1.5),
    fontWeight: 500,
  },
}));

function CloudPaymentRequiredDialog({ open, onClose }) {
  const classes = useStyles();
  const steps = useMemo(
    () => [
      i18n.t("cloudPaymentRequired.step1"),
      i18n.t("cloudPaymentRequired.step2"),
      i18n.t("cloudPaymentRequired.step3"),
      i18n.t("cloudPaymentRequired.step4"),
    ],
    [],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle disableTypography>
        <div className={classes.titleRow}>
          <div className={classes.titleIcon}>
            <PaymentIcon />
          </div>
          <Typography variant="h6" component="h2">
            {i18n.t("cloudPaymentRequired.title")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: "absolute", right: 0, top: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" className={classes.intro}>
          {i18n.t("cloudPaymentRequired.intro")}
        </Typography>

        <Typography variant="subtitle2" className={classes.stepsTitle}>
          {i18n.t("cloudPaymentRequired.stepsTitle")}
        </Typography>

        <ol className={classes.stepsList}>
          {steps.map((step, index) => (
            <li key={`payment-step-${index}`}>
              <Typography variant="body2" component="span">
                {step}
              </Typography>
            </li>
          ))}
        </ol>

        <Typography variant="body2" className={classes.footer}>
          {i18n.t("cloudPaymentRequired.footer")}
        </Typography>

        <Link
          href={META_BILLING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.billingLink}
        >
          {i18n.t("cloudPaymentRequired.billingLink")}
        </Link>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          {i18n.t("cloudPaymentRequired.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function CloudPaymentRequiredDialogHost() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(CLOUD_PAYMENT_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(CLOUD_PAYMENT_REQUIRED_EVENT, handler);
  }, []);

  return (
    <CloudPaymentRequiredDialog open={open} onClose={() => setOpen(false)} />
  );
}

export default CloudPaymentRequiredDialog;
